import {useState} from 'react';
import {authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';

export type PropertyBrief={
 address_id:string;
 matched_address:string|null;
 match:'exact'|'nearest'|'id';
 buildings:{use:string|null;footprint_m2:number|null;floor_area_m2:number|null;floors:number|null;roof_shape:string|null;roof_material:string|null;pool:boolean|null;solar:boolean|null;address_count:number|null;primary:boolean}[];
 parcels:{lot:string|null;plan:string|null;parcel_type:string|null;area_m2:number|null;strata_count:number|null;zone_code:string|null;zone:string|null;zone_url:string|null}[];
 summary:{dwelling:string|null;strata:boolean;floors:number|null;floor_area_m2:number|null;footprint_m2:number|null;lot_area_m2:number|null;zone:string|null;pool:boolean|null;solar:boolean|null;text_en:string};
 attribution:string;
};

/** What the address says before anyone drives there, loaded on request (each brief costs the
 * provider credits, and the record may not be the house the person meant — the matched address is
 * shown so a wrong match is visible, not silent). Building use, zone and roof values are provider
 * data and are shown as they arrive. */
export function PropertyFacts({address,suburb,compact=false}:{address:string;suburb:string|null;compact?:boolean}){
 const {t}=useT();
 const [state,setState]=useState<{status:'idle'}|{status:'loading'}|{status:'ready';brief:PropertyBrief}|{status:'none'}|{status:'notInPlan'}|{status:'failed'}>({status:'idle'});
 if(!address.trim())return null;
 async function load(){
  setState({status:'loading'});
  try{
   const params=new URLSearchParams({address:address.trim(),...(suburb?.trim()?{suburb:suburb.trim()}:{})});
   const res=await fetchImpl(`${env.apiBase}/api/property-brief?${params}`,{headers:await authHeaders()});
   if(res.status===404){setState({status:'none'});return;}
   if(res.status===501){setState({status:'notInPlan'});return;}
   if(!res.ok)throw Error();
   const body=await res.json() as {data:PropertyBrief};
   setState({status:'ready',brief:body.data});
  }catch{setState({status:'failed'});}
 }
 if(state.status==='idle')return <button type="button" className={compact?'text-micro text-primary underline':'text-sm text-primary underline'} onClick={()=>void load()}>{t('property.show')}</button>;
 if(state.status==='loading')return <p role="status" className="text-micro text-muted-foreground">{t('property.loading')}</p>;
 if(state.status==='none')return <p role="status" className="text-micro text-muted-foreground">{t('property.noMatch')}</p>;
 if(state.status==='notInPlan')return <p role="status" className="text-micro text-muted-foreground">{t('property.notInPlan')}</p>;
 if(state.status==='failed')return <p role="status" className="text-micro text-muted-foreground">{t('property.unavailable')}</p>;
 const {brief}=state;const s=brief.summary;const primary=brief.buildings.find(b=>b.primary)??brief.buildings[0];
 const m2=(v:number|null)=>v===null?null:`${Math.round(v).toLocaleString('en-AU')} m²`;
 const yesNo=(v:boolean|null)=>v===null?null:t(v?'property.yes':'property.no');
 const lotPlan=brief.parcels.filter(p=>p.lot||p.plan).map(p=>[p.lot?`Lot ${p.lot}`:null,p.plan].filter(Boolean).join(' ')).join(', ')||null;
 const rows:[string,string|null][]=[
  [t('property.dwelling'),primary?`${primary.use??t('property.building')}${s.strata?` · ${t('property.strata')}`:''}`:t('property.noBuilding')],
  [t('property.floors'),s.floors===null?null:String(s.floors)],
  [t('property.floorArea'),m2(s.floor_area_m2)],
  [t('property.footprint'),m2(s.footprint_m2)],
  [t('property.lotArea'),m2(s.lot_area_m2)],
  [t('property.lotPlan'),lotPlan],
  [t('property.zoning'),s.zone?`${s.zone}${brief.parcels[0]?.zone_code?` (${brief.parcels[0].zone_code})`:''}`:null],
  [t('property.roof'),primary&&(primary.roof_shape||primary.roof_material)?[primary.roof_shape,primary.roof_material].filter(Boolean).join(', '):null],
  [t('property.pool'),yesNo(s.pool)],
  [t('property.solar'),yesNo(s.solar)],
  [t('property.addresses'),primary?.address_count===null||primary?.address_count===undefined?null:String(primary.address_count)],
 ];
 return <div className={compact?'mt-1 max-w-md rounded border bg-card p-2 text-micro':'mt-2 rounded border bg-card p-3 text-sm'} data-testid="property-facts">
  <p className="text-muted-foreground">{t(brief.match==='exact'?'property.matchedExact':brief.match==='nearest'?'property.matchedNearest':'property.matched')} <span className="text-foreground">{brief.matched_address??brief.address_id}</span></p>
  <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">{rows.filter((r):r is [string,string]=>r[1]!==null).map(([k,v])=><div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd>{v}</dd></div>)}</dl>
  <p className="mt-1 text-micro text-muted-foreground">{brief.attribution}</p>
 </div>;
}
